import { Inbox, RotateCw, Search, SearchX, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { QueryErrorView } from '@/components/common/QueryErrorView';
import { SkeletonBlock } from '@/components/common/SkeletonBlock';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import { useSearchReportsQuery } from './reportsApi';
import { ReportRow } from './ReportRow';
import type { ReportSearchArgs } from './reportsApi';
import type { ReportSearchRow, ReportStatus } from './types';

const FILTER_ALL = 'all';

interface PageState {
  count: number;
  hasMore: boolean;
  nextCursor: string | null;
}

const EMPTY_STATE: PageState = { count: 0, hasMore: false, nextCursor: null };

function toState(page: {
  data: ReportSearchRow[];
  pagination: { cursor: string | null; hasMore: boolean };
}): PageState {
  return {
    count: page.data.length,
    hasMore: page.pagination.hasMore,
    nextCursor: page.pagination.cursor,
  };
}

/**
 * Reports browser (design §9). A list — not a DataTable — because rows carry
 * meta-lines and variable-length resource sub-rows (§9.1 H1 justification).
 *
 * Cursor pages accumulate client-side (§10.1): the first page is fetched here;
 * each "Load more" appends a cursor and a `<CursorPage>` child fetches it,
 * reporting its state back. "Showing {n} reports" counts LOADED rows only —
 * totals are impossible with cursor pages (F5, §3.2).
 */
export function ReportsBrowser({ reportProjectId }: { reportProjectId: string }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  const status: ReportStatus | undefined =
    statusParam === 'active' || statusParam === 'resolved' ? statusParam : undefined;

  const [qRaw, setQRaw] = useState('');
  const [q, setQ] = useState('');
  const [nonce, setNonce] = useState(0);
  const [extraCursors, setExtraCursors] = useState<string[]>([]);
  const [pageStates, setPageStates] = useState<Record<string, PageState>>({});

  // Debounce typed text 300ms before refetching (C2 — no per-keystroke requests).
  useEffect(() => {
    const timer = window.setTimeout(() => setQ(qRaw.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [qRaw]);

  const firstPageArgs = useMemo<ReportSearchArgs>(
    () => ({ reportProjectId, status, q: q || undefined, nonce }),
    [reportProjectId, status, q, nonce],
  );
  const firstPage = useSearchReportsQuery(firstPageArgs);

  // Any change of query identity resets the accumulated pages (§9.3: status
  // changes refetch with the cursor cleared; the same applies to search/refresh).
  useEffect(() => {
    setExtraCursors([]);
    setPageStates({});
  }, [firstPageArgs]);

  const handlePageState = useCallback((cursor: string, state: PageState) => {
    setPageStates((prev) => ({ ...prev, [cursor]: state }));
  }, []);

  // §14.1: on filter/search/refresh refetches the NEW query starts loading
  // immediately (fresh args → no cache), but the previously loaded rows must
  // stay visible DIMMED rather than flip to skeletons. They live in local
  // state and are replaced only when a page actually arrives; skeletons render
  // solely on the very first load (no rows ever shown).
  const [firstRows, setFirstRows] = useState<ReportSearchRow[] | null>(null);
  useEffect(() => {
    if (firstPage.data) {
      setFirstRows(firstPage.data.data);
    }
  }, [firstPage.data]);

  const firstState = firstPage.data ? toState(firstPage.data) : EMPTY_STATE;
  const lastCursor = extraCursors[extraCursors.length - 1];
  const lastState = lastCursor !== undefined ? (pageStates[lastCursor] ?? EMPTY_STATE) : firstState;
  const shownCount =
    (firstRows?.length ?? 0) +
    extraCursors.reduce((sum, cursor) => sum + (pageStates[cursor]?.count ?? 0), 0);

  const isRefetching = firstPage.isFetching;
  const showSkeleton = firstPage.isLoading && firstRows === null;
  const isEmpty =
    !showSkeleton &&
    !firstPage.isError &&
    extraCursors.length === 0 &&
    (firstRows?.length ?? 0) === 0;

  function handleStatusChange(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value === FILTER_ALL) {
      next.delete('status');
    } else {
      next.set('status', value);
    }
    setSearchParams(next, { replace: false });
  }

  function handleLoadMore() {
    if (lastState.nextCursor) {
      setExtraCursors((prev) =>
        prev.includes(lastState.nextCursor as string)
          ? prev
          : [...prev, lastState.nextCursor as string],
      );
    }
  }

  function clearSearch() {
    setQRaw('');
    setQ('');
  }

  return (
    <section aria-labelledby="reports-title">
      <h2 id="reports-title" className="text-lg font-semibold">
        Reports
      </h2>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative w-full max-w-md flex-1">
          {isRefetching ? (
            <LoadingSpinner size="sm" className="absolute left-3 top-1/2 -translate-y-1/2" />
          ) : (
            <Search
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
          )}
          <LabellessSearchInput
            value={qRaw}
            maxLength={1024}
            disabled={isRefetching}
            onChange={setQRaw}
          />
          {qRaw ? (
            <button
              type="button"
              aria-label="Clear search"
              disabled={isRefetching}
              onClick={clearSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          ) : null}
        </div>

        <Select value={status ?? FILTER_ALL} onValueChange={handleStatusChange}>
          <SelectTrigger
            id="report-status-filter"
            className="w-40"
            aria-label="Filter by status"
            disabled={isRefetching}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={FILTER_ALL}>All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
          </SelectContent>
        </Select>

        <Tooltip>
          <TooltipTrigger asChild>
            {/* Disabled buttons swallow pointer events; the span keeps the tooltip alive. */}
            <span className="inline-flex">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Refresh reports"
                disabled={isRefetching}
                onClick={() => setNonce((n) => n + 1)}
              >
                <RotateCw className="h-4 w-4" aria-hidden="true" />
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            Re-run the search and refresh download links
          </TooltipContent>
        </Tooltip>
      </div>

      {shownCount > 0 ? (
        <p role="status" aria-live="polite" className="mt-2 text-sm text-muted-foreground">
          Showing {shownCount} report{shownCount === 1 ? '' : 's'}
        </p>
      ) : null}

      <div
        className={cn(
          'mt-3 transition-opacity motion-reduce:transition-none',
          isRefetching && 'pointer-events-none opacity-60',
        )}
      >
        {showSkeleton ? (
          <div className="space-y-2" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <SkeletonBlock key={i} className="h-24 w-full" />
            ))}
          </div>
        ) : firstPage.isError ? (
          <QueryErrorView
            error={firstPage.error}
            label="reports"
            onRetry={() => void firstPage.refetch()}
          />
        ) : isEmpty ? (
          <ReportsEmptyState q={q} status={status} onClearSearch={clearSearch} />
        ) : (
          <div className="divide-y rounded-lg border bg-card">
            {(firstRows ?? []).map((row) => (
              <ReportRow key={row.id} row={row} />
            ))}

            {extraCursors.map((cursor) => (
              <CursorPage
                key={`${nonce}:${cursor}`}
                args={{ ...firstPageArgs, cursor }}
                cursorKey={cursor}
                onState={handlePageState}
              />
            ))}

            {lastState.hasMore ? (
              <div className="px-3 py-4">
                <Button
                  variant="outline"
                  className="mx-auto block w-full max-w-xs"
                  disabled={isRefetching}
                  onClick={handleLoadMore}
                >
                  Load more
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
}

/** The search field — label is visually hidden per the toolbar wireframe (§9.2). */
function LabellessSearchInput({
  value,
  onChange,
  maxLength,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  maxLength: number;
  disabled: boolean;
}) {
  return (
    <Input
      aria-label="Search reports"
      placeholder="Search reports…"
      maxLength={maxLength}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      className="pl-9 pr-10"
    />
  );
}

/** One appended cursor page: fetches its own cache entry and renders its rows. */
function CursorPage({
  args,
  cursorKey,
  onState,
}: {
  args: ReportSearchArgs;
  cursorKey: string;
  onState: (cursor: string, state: PageState) => void;
}) {
  const { data, isLoading, isError, isFetching } = useSearchReportsQuery(args);

  useEffect(() => {
    if (data) {
      onState(cursorKey, toState(data));
    }
  }, [data, cursorKey, onState]);

  if (isLoading || isFetching) {
    return (
      <div className="space-y-2 px-3 py-3" aria-hidden="true">
        <SkeletonBlock className="h-16 w-full" />
      </div>
    );
  }

  if (isError) {
    // Non-blocking: the already-loaded rows stay; "Load more" remains to retry.
    return (
      <p role="alert" className="px-3 py-2 text-sm text-muted-foreground">
        Couldn&apos;t load more reports.
      </p>
    );
  }

  return (
    <>
      {(data?.data ?? []).map((row) => (
        <ReportRow key={row.id} row={row} />
      ))}
    </>
  );
}

/** §9.6 empty states — the "issue already known" copy lives in the search-empty row. */
function ReportsEmptyState({
  q,
  status,
  onClearSearch,
}: {
  q: string;
  status: ReportStatus | undefined;
  onClearSearch: () => void;
}) {
  if (q) {
    return (
      <EmptyState
        icon={<SearchX />}
        title="No matching reports"
        description={`Nothing matches “${q}”. Reports that duplicate an already-known issue are folded into the original report — try the original report's wording, or clear the search.`}
        action={
          <Button variant="link" onClick={onClearSearch}>
            Clear search
          </Button>
        }
      />
    );
  }

  if (status) {
    return (
      <EmptyState
        title={`No ${status === 'active' ? 'Active' : 'Resolved'} reports`}
        description="Nothing here yet."
      />
    );
  }

  return (
    <EmptyState
      icon={<Inbox />}
      title="No reports yet"
      description="Reports sent to this project's environments will appear here."
    />
  );
}
